import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  BarChart3,
  Building2,
  FileText,
  FolderOpen,
  Heart,
  Home,
  Settings,
  Shield,
  Users,
} from "lucide-react";
import { cn } from "../../lib/utils";

const LOGO_URL = "https://cdn.gamma.app/40wtxlv020nnuu2/0bd85e15bbd243ca96d4ed4e00a0f4fa/original/HumaDash-Logo-3.4.25.png";

const sidebarLinks = {
  donor: [
    { href: "/dashboard", label: "Overview", icon: Home },
    { href: "/dashboard/donations", label: "My Donations", icon: Heart },
    { href: "/dashboard/impact", label: "Impact Report", icon: BarChart3 },
    { href: "/dashboard/settings", label: "Settings", icon: Settings },
  ],
  ngo: [
    { href: "/dashboard", label: "Overview", icon: Home },
    { href: "/dashboard/projects", label: "Projects", icon: FolderOpen },
    { href: "/dashboard/donations", label: "Donations", icon: Heart },
    { href: "/dashboard/reports", label: "Reports", icon: FileText },
    { href: "/dashboard/settings", label: "Settings", icon: Settings },
  ],
  admin: [
    { href: "/dashboard", label: "Overview", icon: Home },
    { href: "/dashboard/organizations", label: "Organizations", icon: Building2 },
    { href: "/dashboard/projects", label: "Projects", icon: FolderOpen },
    { href: "/dashboard/donations", label: "Donations", icon: Heart },
    { href: "/dashboard/users", label: "Users", icon: Users },
    { href: "/dashboard/audit", label: "Audit Trail", icon: Shield },
    { href: "/dashboard/analytics", label: "Analytics", icon: BarChart3 },
    { href: "/dashboard/settings", label: "Settings", icon: Settings },
  ],
  government: [
    { href: "/dashboard", label: "Overview", icon: Home },
    { href: "/dashboard/organizations", label: "Organizations", icon: Building2 },
    { href: "/dashboard/audit", label: "Audit Trail", icon: Shield },
    { href: "/dashboard/analytics", label: "Analytics", icon: BarChart3 },
    { href: "/dashboard/reports", label: "Reports", icon: FileText },
  ],
};

export const Sidebar = () => {
  const { user } = useAuth();
  const location = useLocation();

  const links = sidebarLinks[user?.role] || sidebarLinks.donor;

  return (
    <aside className="fixed left-0 top-16 h-[calc(100vh-4rem)] w-64 bg-white border-r border-border overflow-y-auto hidden lg:block">
      <div className="p-4">
        {/* User Info */}
        <div className="mb-6 p-4 rounded-lg bg-primary/5 border border-primary/10">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/15 flex items-center justify-center">
              <span className="text-primary font-semibold">
                {user?.name?.charAt(0)?.toUpperCase()}
              </span>
            </div>
            <div className="overflow-hidden">
              <p className="text-sm font-medium text-foreground truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
            </div>
          </div>
        </div>

        {/* Navigation Links */}
        <nav className="space-y-1">
          {links.map((link) => {
            const isActive = location.pathname === link.href;
            return (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`sidebar-${link.label.toLowerCase().replace(" ", "-")}`}
                className={cn(
                  "flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all",
                  isActive
                    ? "bg-primary/10 text-primary border border-primary/20"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                <link.icon className="h-4 w-4" />
                {link.label}
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Footer */}
      <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border">
        <Link to="/public-dashboard" className="flex items-center gap-2 text-xs text-muted-foreground hover:text-primary transition-colors">
          <img src={LOGO_URL} alt="HumaDash" className="h-6 w-auto opacity-60" />
          <span>Public Dashboard</span>
        </Link>
      </div>
    </aside>
  );
};

export default Sidebar;
